import { useState, useCallback } from 'react'
import { slide as Slide } from 'react-burger-menu'
import { Link, useLocation, useNavigate } from 'react-router-dom'
import styled from 'styled-components'

import { signout } from 'commons/firebase-auth'
import { useAuthProvider } from 'contexts/auth'
import {
  JWT_TOKEN_KEY_NAME,
  LOGIN_USER_MODE,
  PRIVACY_POLICY_URL,
  TERMS_URL,
} from 'constants/common'
import ROUTING_PATH from 'constants/route'
import { UserRole } from 'schema/user'
import getLayer from 'styles/layer'
import ToolListModal from './tool-list-modal'
import { HeaderHeightPx } from './header'

const Container = styled.div`
  .bm-overlay {
    z-index: ${getLayer('menu')} !important;
  }

  .bm-burger-button {
    position: fixed;
    height: 16px;
    width: 22px;
    right: 22px;
    top: 22px;
  }

  .bm-cross-button {
    width: 18px !important;
    height: 18px !important;
    right: 23px !important;
    top: 32px !important;
  }

  .bm-menu {
    margin-top: ${HeaderHeightPx};
    border-top: 1px solid ${(p) => p.theme.GROBAL_MENU_BORDER};
    background-color: #ffffff;
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.05);
    position: relative;
    height: calc(100vh - ${HeaderHeightPx});
    padding-bottom: ${HeaderHeightPx};
    box-sizing: border-box;
  }

  .bm-item {
    position: relative;
    display: flex;
    align-items: center;
    justify-content: space-between;
    width: 100%;
    padding: 16px 20px;
    background-color: transparent;
    border: none;
    box-sizing: border-box;
    color: #555;
    font-size: ${(p) => p.theme.font.size.m};
    text-align: left;
    text-decoration: none;
    transition: background-color 0.2s ease;

    &:hover {
      background-color: #f9f9f9;
      color: #333;
    }

    .menu-text {
      display: inline-block;
      width: calc(100% - 30px);
      text-align: left;
      vertical-align: middle;
    }

    img {
      width: 24px;
      height: 24px;
      vertical-align: middle;
    }
  }

  .bm-item.logout-section {
    position: absolute;
    bottom: ${HeaderHeightPx};
    left: 0;
    right: 0;
    margin-top: 0;
  }

  @media only screen and (max-width: ${(p) => p.theme.vp}px) {
    .bm-burger-button {
      top: 20px;
    }

    .bm-cross-button {
      top: 22px !important;
    }

    .bm-menu {
      margin-top: ${HeaderHeightPx};
    }

    .bm-item {
      padding: 14px 16px;
    }
  }
`

const GlobalMenu = () => {
  const [isOpen, setIsOpen] = useState<boolean>(false)
  const [isToolListModalOpen, setIsToolListModalOpen] = useState<boolean>(false)
  const { currentUser, claims } = useAuthProvider()
  const { pathname } = useLocation()
  const navigate = useNavigate()

  const handleClose = useCallback(() => {
    setIsOpen(false)
  }, [])

  const handleToolListOpen = useCallback(() => {
    setIsToolListModalOpen(true)
    handleClose()
  }, [handleClose])

  const handleToolListClose = useCallback(() => {
    setIsToolListModalOpen(false)
  }, [])

  const handleStateChange = useCallback((state: any) => {
    setIsOpen(state.isOpen)
  }, [])

  const handleSignOut = useCallback(() => {
    signout().then(() => {
      localStorage.removeItem(LOGIN_USER_MODE)
      localStorage.removeItem(JWT_TOKEN_KEY_NAME)
      if (pathname.includes('owner')) {
        navigate(ROUTING_PATH.B0101)
      } else if (pathname.includes('agent')) {
        navigate(ROUTING_PATH.C0101)
      } else {
        navigate(ROUTING_PATH.A0101)
      }
    })
    handleClose()
  }, [pathname, handleClose, navigate])

  return (
    <Container>
      <Slide
        right
        width={297}
        isOpen={isOpen}
        onStateChange={(state: { isOpen: boolean }) => handleStateChange(state)}
        customBurgerIcon={
          !isOpen ? <img src="/image/menu.svg" alt="menu" /> : false
        }
        customCrossIcon={<img src="/image/close.svg" alt="menu" />}
      >
        {!currentUser && (
          <Link to={ROUTING_PATH.A0101} onClick={() => handleClose()}>
            ログイン
          </Link>
        )}
        {currentUser && claims?.role === UserRole.OWNER && (
          <Link to={ROUTING_PATH.B0201} onClick={() => handleClose()}>
            <>
              <span className="menu-text">物件一覧</span>
              <img src="/image/arrow-right-orange.svg" alt="物件一覧" />
            </>
          </Link>
        )}
        {currentUser && claims?.role === UserRole.AGENT && (
          <Link to={ROUTING_PATH.C0201} onClick={() => handleClose()}>
            <>
              <span className="menu-text">物件一覧</span>
              <img src="/image/arrow-right-orange.svg" alt="物件一覧" />
            </>
          </Link>
        )}
        {currentUser && claims?.role === UserRole.OWNER && (
          <Link to={ROUTING_PATH.B0404} onClick={() => handleClose()}>
            <>
              <span className="menu-text">通知設定</span>
              <img src="/image/arrow-right-orange.svg" alt="通知設定" />
            </>
          </Link>
        )}
        {/* {currentUser && claims?.role === UserRole.OWNER && (
          <Link to={ROUTING_PATH.B0405} onClick={() => handleClose()}>
            <>
              <span className="menu-text">ログイン設定</span>
              <img src="/image/arrow-right-orange.svg" alt="ログイン設定" />
            </>
          </Link>
        )} */}
        {currentUser && claims?.role === UserRole.AGENT && (
          <a
            href="https://www.notion.so/terass/Terass-Portal-9c3da7651b5e444c8dabbe617e22aad6?pvs=4#706e3f59e7ad4273b95f81909ffc5f4b"
            onClick={() => handleClose()}
            target="_blank"
            rel="noreferrer"
          >
            <span className="menu-text">よくある質問</span>
            <img src="/image/outside-orange.svg" alt="よくある質問" />
          </a>
        )}

        {currentUser && claims?.role === UserRole.OWNER && (
          <a
            href="https://terassinc.notion.site/Terass-Portal-SUUMO-3d4794cc89dc45cba4c085cbb9abc55a"
            onClick={() => handleClose()}
            target="_blank"
            rel="noreferrer"
          >
            <span className="menu-text">SUUMOデータ確認方法</span>
            <img src="/image/outside-orange.svg" alt="SUUMOデータ確認方法" />
          </a>
        )}
        <a
          href={PRIVACY_POLICY_URL}
          onClick={() => handleClose()}
          target="_blank"
          rel="noreferrer"
        >
          <span className="menu-text">プライバシーポリシー</span>
          <img src="/image/outside-orange.svg" alt="プライバシーポリシー" />
        </a>
        <a
          href={TERMS_URL}
          onClick={() => handleClose()}
          target="_blank"
          rel="noreferrer"
        >
          <span className="menu-text">利用規約</span>
          <img src="/image/outside-orange.svg" alt="利用規約" />
        </a>
        {currentUser && claims?.role === UserRole.AGENT && (
          <button onClick={handleToolListOpen}>
            <span className="menu-text">ツール一覧</span>
            <img src="/image/arrow-right-orange.svg" alt="ツール一覧" />
          </button>
        )}
        {currentUser && (
          <button className="logout-section" onClick={() => handleSignOut()}>
            <span className="menu-text">ログアウト</span>
            <img src="/image/logout.svg" alt="ログアウト" />
          </button>
        )}
      </Slide>
      <ToolListModal
        isOpen={isToolListModalOpen}
        onClose={handleToolListClose}
      />
    </Container>
  )
}

export default GlobalMenu
